import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { NamedLink, LinkedLogo } from '../../components';
import LayoutComposer from '../LayoutComposer/LayoutComposer';
import { IconHome, IconTag, IconBox, IconMail, IconHeart, IconGear } from './AccountIcons';

import css from './AccountShell.module.css';

const navItems = hasListings => [
  { key: 'dashboard', Icon: IconHome, labelId: 'AccountShell.dashboard', name: 'DashboardPage' },
  {
    key: 'listings',
    Icon: IconTag,
    labelId: 'AccountShell.myListings',
    name: 'ManageListingsPage',
  },
  {
    key: 'orders',
    Icon: IconBox,
    labelId: 'AccountShell.orders',
    name: 'OrdersPage',
    params: { tab: hasListings ? 'selling' : 'buying' },
  },
  {
    key: 'messages',
    Icon: IconMail,
    labelId: 'AccountShell.messages',
    name: 'InboxPage',
    params: { tab: hasListings ? 'sales' : 'orders' },
    showBadge: true,
  },
  { key: 'watchlist', Icon: IconHeart, labelId: 'AccountShell.watchlist', name: 'WishlistPage' },
  {
    key: 'settings',
    Icon: IconGear,
    labelId: 'AccountShell.accountSettings',
    name: 'ContactDetailsPage',
  },
];

/**
 * Navy sidebar layout shared by all account pages (dashboard mockup).
 *
 * @component
 * @param {Object} props
 * @param {'dashboard'|'listings'|'orders'|'messages'|'watchlist'|'settings'} props.activeItem
 * @param {ReactNode} props.topbar
 * @param {ReactNode?} props.footer
 * @param {string?} props.className
 * @param {string?} props.mainClassName
 * @param {ReactNode} props.children
 */
const AccountShell = props => {
  const { activeItem, topbar, footer, className, mainClassName, children } = props;
  const intl = useIntl();
  const notificationCount = useSelector(state => state.user?.currentUserNotificationCount || 0);
  const hasListings = useSelector(state => !!state.user?.currentUserHasListings);

  return (
    <LayoutComposer areas={'topbar\nmain\nfooter'} className={classNames(css.root, className)}>
      {({ Topbar, Main, Footer }) => (
        <>
          <Topbar as="header">{topbar}</Topbar>
          <Main as="div" className={css.container}>
            <nav
              className={css.sidebar}
              aria-label={intl.formatMessage({ id: 'AccountShell.navLabel' })}
            >
              <ul className={css.navList}>
                {navItems(hasListings).map(
                  ({ key, Icon, labelId, name, params, to, showBadge }) => {
                    const isActive = key === activeItem;
                    const linkClassName = classNames(css.navLink, {
                      [css.navLinkActive]: isActive,
                    });
                    const content = (
                      <>
                        <Icon className={css.navIcon} />
                        <span className={css.navLabel}>
                          <FormattedMessage id={labelId} />
                        </span>
                        {showBadge && notificationCount > 0 ? (
                          <span className={css.badge}>{notificationCount}</span>
                        ) : null}
                      </>
                    );
                    return (
                      <li key={key}>
                        <NamedLink
                          name={name}
                          params={params}
                          to={to}
                          className={linkClassName}
                          activeClassName=""
                          aria-current={isActive ? 'page' : undefined}
                        >
                          {content}
                        </NamedLink>
                      </li>
                    );
                  }
                )}
              </ul>
              <div className={css.sidebarFooter}>
                <p className={css.buySellTrade}>
                  <FormattedMessage id="AccountShell.buySellTrade" />
                </p>
                <p className={css.tagline}>
                  <FormattedMessage id="AccountShell.collectiblesTagline" />
                </p>
                <hr className={css.footerRule} />
                <LinkedLogo className={css.footerLogo} layout="desktop" />
              </div>
            </nav>
            <main className={classNames(css.main, mainClassName)}>{children}</main>
          </Main>
          {footer ? <Footer>{footer}</Footer> : null}
        </>
      )}
    </LayoutComposer>
  );
};

export default AccountShell;
